import React, { useState, useMemo } from "react";
import { Drawer, Box, Typography, TextField, Button, Grid, IconButton } from "@mui/material";
import PropTypes from "prop-types";
import CloseIcon from "@mui/icons-material/Close";
import { Snackbar, Alert } from "@mui/material";
export default function MarksDrawer({
  open,
  onClose,
  questions = [],
  marks = {},
  setMarks,
  setIsFinal,
  onSave,
  onVerify,
  visitedPages = [],
  currentPage = 1,
  onPageSelect,
}) {
  /* const [currentPage, setCurrentPage] = useState(1);
  const [visitedPages, setVisitedPages] = useState([]); */
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  // 🔹 When page clicked, set current page and mark it visited
  const handlePageClick = (page) => {
    setCurrentPage(page);
    setVisitedPages((prev) => (prev.includes(page) ? prev : [...prev, page]));
  };
  const user = JSON.parse(sessionStorage.getItem("user"));
  const roleId = user.core_user_roles?.[0]?.role_id;
  const handleMarkChange = (template_question_id, value) => {
    setMarks((prev) => ({
      ...prev,
      [template_question_id]: value === "" ? "" : parseFloat(value),
    }));
  };

  // 🔹 Compute section totals + highlighted question IDs
  const { sectionTotals, countedQuestionIds } = useMemo(() => {
    const groupedBySection = questions.reduce((acc, q) => {
      acc[q.section_label] = acc[q.section_label] || [];
      acc[q.section_label].push(q);
      return acc;
    }, {});

    const totals = {};
    const countedIds = new Set();
    for (const [section, qs] of Object.entries(groupedBySection)) {
      // ✅ Always group by question_number (ignore sub_label)
      const grouped = {};
      qs.forEach((q) => {
        const key = String(q.question_number);
        const obtained = parseFloat(marks[q.template_question_id]) || 0;

        if (!grouped[key]) grouped[key] = [];
        grouped[key].push({ ...q, obtained });
      });

      // ✅ Within each group (like 9a/9b), take highest marks
      const bestFromEach = Object.values(grouped).map((group) =>
        group.reduce((max, cur) => (cur.obtained > max.obtained ? cur : max), group[0])
      );

      // ✅ Sort by marks and take top attempt_count
      const sorted = [...bestFromEach].sort((a, b) => b.obtained - a.obtained);
      const attemptCount = qs[0]?.attempt_count || sorted.length;
      const topQs = sorted.slice(0, attemptCount);

      // ✅ Compute total marks per section
      const total = topQs.reduce((sum, q) => sum + q.obtained, 0);
      totals[section] = total;

      // ✅ Highlight counted questions
      topQs.forEach((q) => countedIds.add(q.template_question_id));
    }

    return { sectionTotals: totals, countedQuestionIds: countedIds };
  }, [questions, marks]);

  const grandTotal = Object.values(sectionTotals).reduce((a, b) => a + b, 0);
  const totalPages = 24;

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 310,
          p: { xs: 2, sm: 1 },
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          borderTopRightRadius: 12,
          borderBottomRightRadius: 12,
          overflow: "hidden",
        },
      }}
      ModalProps={{
        keepMounted: true,
        disableEscapeKeyDown: true, // ✅ prevent ESC from closing
        hideBackdrop: true, // ✅ avoid dark overlay (optional)
      }}
    >
      <Box sx={{ width: 300, p: 0.5, bgcolor: "#efdfdfff", height: "100%", overflowY: "auto" }}>
        <Box position="relative" mb={2}>
          <Typography variant="h5" sx={{ fontWeight: "bold", textAlign: "center" }}>
            Script Valuation
          </Typography>

          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              color: "text.secondary",
              "&:hover": {
                color: "error.main",
                bgcolor: "transparent",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Section-wise Marks Entry */}
        {Object.entries(
          questions.reduce((acc, q) => {
            acc[q.section_label] = acc[q.section_label] || [];
            acc[q.section_label].push(q);
            return acc;
          }, {})
        ).map(([section, qs]) => (
          <Box key={section} sx={{ mb: 3, borderBottom: "1px solid #ddd", pb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "primary.main" }}>
              SECTION-{section}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
              Answer {qs[0]?.attempt_count || qs.length} Questions
            </Typography>

            <Grid container spacing={1}>
              {qs.map((q) => {
                const isCounted = countedQuestionIds.has(q.template_question_id);
                const hasMarks = Number(marks[q.template_question_id]) > 0;
                return (
                  <Grid item xs={4} key={q.template_question_id}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0,
                        border: "1px solid #e74444ff",
                        p: 0.7,
                        borderRadius: 2,

                        bgcolor: isCounted
                          ? "#e8f5e9" // counted questions
                          : hasMarks
                          ? "#eabb9eff" // marks entered
                          : "transparent",

                        borderColor: hasMarks ? "primary.main" : "#eee",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <Typography
                        variant="body3"
                        sx={{ minWidth: 25, fontWeight: 500, fontSize: "0.95rem" }}
                      >
                        {q.question_number}
                        {q.sub_label ? q.sub_label : ""}.
                      </Typography>
                      <TextField
                        type="number"
                        size="medium"
                        inputProps={{
                          max: q.marks,
                          min: 0,
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                        }}
                        value={marks[q.template_question_id] ?? ""}
                        //disabled={roleId === 1}
                        onChange={(e) => {
                          //if (roleId === 1) return;

                          let val = parseFloat(e.target.value);
                          if (isNaN(val)) val = "";
                          else if (val > q.marks) val = q.marks;
                          else if (val <= 0) val = 0;

                          handleMarkChange(q.template_question_id, val);
                        }}
                        label={`/${q.marks}`}
                        sx={{
                          width: 60,
                          fontWeight: 900,
                          fontSize: "2.0rem",
                          bgcolor: hasMarks ? "#e3f2fd" : "transparent",

                          /* 🔴 REMOVE SCROLLBAR ARROWS */
                          "& input[type=number]": {
                            MozAppearance: "textfield", // Firefox
                          },
                          "& input[type=number]::-webkit-outer-spin-button": {
                            WebkitAppearance: "none",
                            margin: 0,
                          },
                          "& input[type=number]::-webkit-inner-spin-button": {
                            WebkitAppearance: "none",
                            margin: 0,
                          },
                        }}
                      />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}

        {/* Summary Report */}
        <Box
          sx={{
            border: "2px solid #ddd",
            borderRadius: 1,
            bgcolor: "#fff8f2",
            mt: 2,
            p: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ bgcolor: "#ffe5cc", p: 1, fontWeight: "bold", mb: 1 }}
          >
            Summary Report
          </Typography>
          <table style={{ width: "100%", fontSize: 13 }}>
            <thead>
              <tr>
                <th align="left">Part</th>
                <th align="right">Total Marks</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(sectionTotals).map(([section, total]) => (
                <tr key={section}>
                  <td>Section-{section}</td>
                  <td align="right">{total}</td>
                </tr>
              ))}
              <tr style={{ fontWeight: "bold", borderTop: "1px solid #ddd" }}>
                <td>Grand Total</td>
                <td align="right">{grandTotal}</td>
              </tr>
            </tbody>
          </table>
        </Box>

        {/* Pages Visited */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1, textAlign: "center" }}>
            Pages Visited
          </Typography>
          <Grid container spacing={1}>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
              const isVisited = visitedPages.includes(page);
              const isCurrent = page === currentPage;
              return (
                <Grid item xs={2} key={page}>
                  <Button
                    variant={isCurrent ? "contained" : "outlined"}
                    sx={{
                      minWidth: 30,
                      height: 30,
                      p: 0,
                      borderRadius: "50%",
                      fontSize: 12,
                      borderColor: isVisited ? "success.main" : "grey.400",
                      bgcolor: isCurrent
                        ? "error.main"
                        : isVisited
                        ? "success.light"
                        : "transparent",
                      color: isCurrent
                        ? "#fff"
                        : isVisited
                        ? "success.contrastText" // ✅ ensures readable text on green background
                        : "text.secondary",
                      "&:hover": {
                        bgcolor: isCurrent ? "error.dark" : isVisited ? "success.main" : "grey.100",
                      },
                    }}
                    onClick={() => onPageSelect && onPageSelect(page)}
                  >
                    {page}
                  </Button>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Save Button */}
        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#3079dfff",
              color: "#fff",
              "&:hover": { backgroundColor: "#000" },
            }}
            fullWidth
            onClick={() => {
              const totalPages = 24;
              if (roleId === 6) {
                // 🚨 check all pages visited
                const unvisited = [];
                for (let i = 1; i <= totalPages; i++) {
                  if (!visitedPages.includes(i)) unvisited.push(i);
                }

                if (unvisited.length > 0) {
                  setSnackbarMsg(
                    `Please visit all pages before saving marks! Unvisited pages: ${unvisited.join(
                      ", "
                    )}`
                  );
                  setSnackbarOpen(true);
                  return;
                }

                setIsFinal(true);
                onSave(sectionTotals, grandTotal, true);
              } else if (roleId === 1) {
                setIsFinal(true);
                (async () => {
                  await onSave(sectionTotals, grandTotal, true); // ✅ wait till save completes
                  await onVerify(); // ✅ verify AFTER save
                })();
              }
            }}
          >
            {roleId === 6 ? "Save Marks" : roleId === 1 ? "Verify Marks" : "Action"}
          </Button>
        </Box>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="error"
          variant="filled"
          sx={{
            width: "100%",
            whiteSpace: "pre-line",
            wordBreak: "break-word",
            maxWidth: "320px", // fits in mobile view
            textAlign: "left",
          }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Drawer>
  );
}

MarksDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  questions: PropTypes.array.isRequired,
  marks: PropTypes.object.isRequired,
  setMarks: PropTypes.func.isRequired,
  setIsFinal: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onVerify: PropTypes.func.isRequired,
  onPageSelect: PropTypes.func,
  visitedPages: PropTypes.array,
  currentPage: PropTypes.number,
};
